import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import api from "../api";
import { Eye, EyeOff } from "lucide-react";

const ROLES = [
  "ADMIN",
  "PRODUCTION_MANAGER",
  "PRODUCTION_WORKER",
  "QC_INSPECTOR",
  "QC_MANAGER",
  "INVENTORY_MANAGER",
  "HR_MANAGER",
  "HR_STAFF",
  "HR_ADMIN",
  "HR_EXECUTIVE",
  "HR",
  "ACCOUNTANT",
  "SALES_MANAGER",
  "SALES_STAFF",
];

const Page = styled.div`
  background: #f8fafc;
  min-height: 100vh;
  padding: 16px;
  color: #0f172a;
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;
const Title = styled.h2`
  margin: 0;
  font-size: 1.6rem;
  color: #0f172a;
  font-weight: 800;
`;
const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
`;
const Filters = styled(Card)`
  padding: 12px;
  margin-bottom: 12px;
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr auto;
  gap: 10px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;
const Input = styled.input`
  width: 100%;
  min-height: 44px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  color: #0f172a;
  background: #fff;
`;
const Select = styled.select`
  width: 100%;
  min-height: 44px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  color: #0f172a;
  background: #fff;
`;
const Btn = styled.button`
  min-height: 44px;
  border: none;
  border-radius: 10px;
  padding: 0 14px;
  font-weight: 700;
  cursor: pointer;
  background: ${(p) => (p.$secondary ? "#e2e8f0" : "#2563eb")};
  color: ${(p) => (p.$secondary ? "#0f172a" : "#fff")};
`;
const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;
const Table = styled.table`
  width: 100%;
  min-width: 980px;
  border-collapse: collapse;
  th,
  td {
    border-bottom: 1px solid #e2e8f0;
    padding: 12px 10px;
    text-align: left;
    color: #0f172a;
    vertical-align: middle;
  }
  th {
    color: #64748b;
    font-size: 0.76rem;
    text-transform: uppercase;
  }
`;
const Badge = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 4px 10px;
  display: inline-block;
  background: ${(p) => p.$bg || "#f1f5f9"};
  color: ${(p) => p.$color || "#334155"};
`;
const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;
const ActionLink = styled.button`
  border: none;
  background: none;
  color: ${(p) => p.$color || "#2563eb"};
  font-weight: 700;
  cursor: pointer;
`;
const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-top: 1px solid #e2e8f0;
`;
const ModalBg = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1200;
`;
const Modal = styled.div`
  width: 100%;
  max-width: 760px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 22px 46px rgba(2, 6, 23, 0.25);
  max-height: 92vh;
  overflow-y: auto;
  @media (max-width: 768px) {
    max-width: 100%;
    height: 100vh;
    border-radius: 0;
  }
`;
const ModalHead = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const ModalBody = styled.div`
  padding: 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 0.8rem;
  color: #334155;
  font-weight: 700;
`;
const Field = styled.div``;
const PasswordWrap = styled.div`
  position: relative;
`;
const EyeBtn = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;
const Skeleton = styled.div`
  height: ${(p) => p.$h || 44}px;
  border-radius: 8px;
  background: linear-gradient(90deg, #e2e8f0 25%, #f8fafc 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.3s infinite;
  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;
const ErrorBox = styled.div`
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  font-weight: 700;
  font-size: 0.86rem;
`;

const roleColors = {
  ADMIN: ["#fee2e2", "#991b1b"],
  PRODUCTION_MANAGER: ["#dbeafe", "#1e40af"],
  PRODUCTION_WORKER: ["#dbeafe", "#1d4ed8"],
  QC_INSPECTOR: ["#ffedd5", "#9a3412"],
  QC_MANAGER: ["#ffedd5", "#7c2d12"],
  INVENTORY_MANAGER: ["#dcfce7", "#166534"],
  HR_MANAGER: ["#f3e8ff", "#6b21a8"],
  HR_STAFF: ["#f3e8ff", "#7e22ce"],
  HR_ADMIN: ["#f3e8ff", "#6b21a8"],
  HR_EXECUTIVE: ["#f3e8ff", "#7e22ce"],
  HR: ["#f3e8ff", "#7e22ce"],
  ACCOUNTANT: ["#e0f2fe", "#0c4a6e"],
  SALES_MANAGER: ["#fef3c7", "#92400e"],
  SALES_STAFF: ["#fef3c7", "#a16207"],
};

function UserManagement() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [roleOptions, setRoleOptions] = useState(ROLES);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [branchId, setBranchId] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [resetPassword, setResetPassword] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "PRODUCTION_WORKER",
    branch_id: "",
    is_active: true,
  });

  const fetchBranches = async () => {
    const res = await api.get("/admin/branches");
    setBranches(res?.data?.data || []);
  };

  const fetchRoleOptions = async () => {
    const res = await api.get("/admin/role-matrix");
    const opts = Array.isArray(res?.data?.roles) ? res.data.roles : [];
    const merged = Array.from(
      new Set(
        [...ROLES, ...opts]
          .map((r) => String(r || "").trim().toUpperCase())
          .filter(Boolean)
      )
    );
    if (merged.length) {
      setRoleOptions(merged);
    }
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { page, limit };
      if (search.trim()) params.search = search.trim();
      if (role) params.role = role;
      if (branchId) params.branch_id = Number(branchId);
      const res = await api.get("/admin/users", { params });
      setUsers(res?.data?.data || []);
      setPagination(res?.data?.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load users.");
    } finally {
      setLoading(false);
    }
  }, [branchId, limit, page, role, search]);

  useEffect(() => {
    fetchBranches().catch(() => {});
    fetchRoleOptions().catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 250);
    return () => clearTimeout(timer);
  }, [fetchUsers]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: "",
      email: "",
      password: "",
      role: "PRODUCTION_WORKER",
      branch_id: "",
      is_active: true,
    });
    setShowPassword(false);
    setResetPassword("");
    setShowResetPassword(false);
    setModalOpen(true);
  };

  const openEdit = (user) => {
    setEditing(user);
    setForm({
      name: user.name || "",
      email: user.email || "",
      password: "",
      role: user.role || "PRODUCTION_WORKER",
      branch_id: user.branch_id || "",
      is_active: !!user.is_active,
    });
    setShowPassword(false);
    setResetPassword("");
    setShowResetPassword(false);
    setModalOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      if (editing) {
        await api.put(`/admin/users/${editing.id}`, {
          role: form.role,
          branch_id: form.branch_id ? Number(form.branch_id) : null,
          is_active: !!form.is_active,
        });
        if (resetPassword.trim()) {
          await api.put(`/admin/users/${editing.id}/password`, { password: resetPassword.trim() });
        }
      } else {
        await api.post("/admin/users", {
          name: form.name,
          email: form.email,
          password: form.password,
          role: form.role,
          branch_id: form.branch_id ? Number(form.branch_id) : null,
        });
      }
      setModalOpen(false);
      fetchUsers();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save user.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (user) => {
    try {
      await api.put(`/admin/users/${user.id}`, {
        is_active: !user.is_active,
      });
      fetchUsers();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update status.");
    }
  };

  const rows = useMemo(() => users, [users]);

  return (
    <Page>
      {error ? <ErrorBox>{error}</ErrorBox> : null}
      <Header>
        <Title>User Management</Title>
        <Btn onClick={openCreate}>+ Create User</Btn>
      </Header>

      <Filters>
        <Input
          placeholder="Search by name or email"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />
        <Select
          value={role}
          onChange={(e) => {
            setPage(1);
            setRole(e.target.value);
          }}
        >
          <option value="">All Roles</option>
          {roleOptions.map((r) => (
            <option value={r} key={r}>
              {r}
            </option>
          ))}
        </Select>
        <Select
          value={branchId}
          onChange={(e) => {
            setPage(1);
            setBranchId(e.target.value);
          }}
        >
          <option value="">All Branches</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </Select>
        <Btn $secondary onClick={() => {
          setSearch("");
          setRole("");
          setBranchId("");
          setPage(1);
        }}>
          Reset
        </Btn>
      </Filters>

      <Card>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Branch</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, idx) => (
                  <tr key={idx}>
                    <td colSpan={7}>
                      <Skeleton />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ color: "#64748b", textAlign: "center" }}>
                    No users found.
                  </td>
                </tr>
              ) : (
                rows.map((u) => {
                  const colors = roleColors[u.role] || ["#e2e8f0", "#334155"];
                  return (
                    <tr key={u.id}>
                      <td>{u.name}</td>
                      <td>{u.email}</td>
                      <td>
                        <Badge $bg={colors[0]} $color={colors[1]}>
                          {u.role}
                        </Badge>
                      </td>
                      <td>{u.branch_name || "-"}</td>
                      <td>
                        <Badge $bg={u.is_active ? "#dcfce7" : "#e5e7eb"} $color={u.is_active ? "#166534" : "#374151"}>
                          {u.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td>{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "-"}</td>
                      <td>
                        <Actions>
                          <ActionLink onClick={() => openEdit(u)}>Edit</ActionLink>
                          {!u.is_primary_admin && (
                            <ActionLink $color={u.is_active ? "#dc2626" : "#16a34a"} onClick={() => toggleActive(u)}>
                              {u.is_active ? "Deactivate" : "Activate"}
                            </ActionLink>
                          )}
                        </Actions>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </TableWrap>
        <Footer>
          <div style={{ color: "#64748b" }}>Total: {pagination.total || 0}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn $secondary disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Prev
            </Btn>
            <span style={{ padding: "10px 6px", color: "#334155" }}>
              Page {pagination.page || page} / {pagination.pages || 1}
            </span>
            <Btn $secondary
              disabled={(pagination.page || page) >= (pagination.pages || 1)}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Btn>
          </div>
        </Footer>
      </Card>

      {modalOpen && (
        <ModalBg onClick={() => setModalOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHead>
              <strong style={{ color: "#0f172a" }}>{editing ? "Edit User" : "Create User"}</strong>
              <ActionLink onClick={() => setModalOpen(false)} $color="#64748b">
                Close
              </ActionLink>
            </ModalHead>
            <form onSubmit={submit}>
              <ModalBody>
                <Field>
                  <Label>Name</Label>
                  <Input
                    required
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    disabled={!!editing}
                  />
                </Field>
                <Field>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    disabled={!!editing}
                  />
                </Field>
                {!editing ? (
                  <Field>
                    <Label>Password</Label>
                    <PasswordWrap>
                      <Input
                        type={showPassword ? "text" : "password"}
                        required
                        value={form.password}
                        onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                        style={{ paddingRight: 42 }}
                      />
                      <EyeBtn type="button" onClick={() => setShowPassword((prev) => !prev)} aria-label="Toggle password visibility">
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </EyeBtn>
                    </PasswordWrap>
                  </Field>
                ) : <div />}
                <Field>
                  <Label>Role</Label>
                  <Select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
                    {roleOptions.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field>
                  <Label>Branch</Label>
                  <Select value={form.branch_id} onChange={(e) => setForm((f) => ({ ...f, branch_id: e.target.value }))}>
                    <option value="">Unassigned</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                {editing ? (
                  <Field>
                    <Label>Status</Label>
                    <Select
                      value={form.is_active ? "active" : "inactive"}
                      onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.value === "active" }))}
                      disabled={!!editing?.is_primary_admin}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Select>
                    {editing?.is_primary_admin ? (
                      <div style={{ marginTop: 6, fontSize: "0.78rem", color: "#64748b", fontWeight: 600 }}>
                        Main admin cannot be deactivated.
                      </div>
                    ) : null}
                  </Field>
                ) : <div />}
                {editing ? (
                  <Field>
                    <Label>Reset Password</Label>
                    <PasswordWrap>
                      <Input
                        type={showResetPassword ? "text" : "password"}
                        value={resetPassword}
                        onChange={(e) => setResetPassword(e.target.value)}
                        placeholder="Leave blank to keep current password"
                        style={{ paddingRight: 42 }}
                      />
                      <EyeBtn type="button" onClick={() => setShowResetPassword((prev) => !prev)} aria-label="Toggle reset password visibility">
                        {showResetPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </EyeBtn>
                    </PasswordWrap>
                  </Field>
                ) : <div />}
              </ModalBody>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: 16, borderTop: "1px solid #e2e8f0" }}>
                <Btn type="button" $secondary onClick={() => setModalOpen(false)}>
                  Cancel
                </Btn>
                <Btn type="submit" disabled={submitting}>
                  {submitting ? "Saving..." : editing ? "Update User" : "Create User"}
                </Btn>
              </div>
            </form>
          </Modal>
        </ModalBg>
      )}
    </Page>
  );
}

export default UserManagement;
